import { useSyncExternalStore } from 'react';
import { MotifModal } from '@ey-xd/motif-wc-react';
import { sessionDurationManager, type SessionCapState } from '@/auth/session-duration';

/**
 * Absolute session-cap warning modal — mirrors legacy
 * `session-cap-warning.component.html`. Shown 60s before the session's maximum
 * duration (`login.time` + `maxSessionDurationHours`) elapses. "OK" dismisses
 * the notice (the session still ends at expiry); "SIGN OUT" logs out now. If no
 * action is taken, the session terminates automatically at the countdown's end.
 *
 * Mounted at shell level; renders nothing while `status !== 'warned'`.
 */
export function SessionCapModal() {
  const state = useSyncExternalStore<SessionCapState>(
    (cb) => sessionDurationManager.subscribe(cb),
    () => sessionDurationManager.getState(),
    () => sessionDurationManager.getState(),
  );

  const open = state.status === 'warned';
  const secondsLeft = Math.max(0, Math.ceil(state.remainingMs / 1000));

  return (
    <div className="motif-theme-light">
      <MotifModal
        open={open}
        onModalClose={() => sessionDurationManager.dismiss()}
        size="md"
        showFeaturedIcon={false}
        style={
          {
            '--modal-overflow-y': 'hidden',
            '--modal-body-overflow-y': 'hidden',
            '--modal-footer-width': '100%',
          } as React.CSSProperties
        }
      >
        <div slot="header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              border: '2px solid #d32f2f',
              color: '#d32f2f',
              fontWeight: 'bold',
              fontSize: '18px',
            }}
          >
            &#9432;
          </span>
          <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>Session Ending Soon</span>
        </div>
        <div slot="body">
          <p style={{ margin: '0 0 16px' }}>Your session has reached its maximum duration.</p>
          <p style={{ margin: '0 0 16px' }}>
            You can dismiss this notice by clicking{' '}
            <span
              role="button"
              tabIndex={0}
              onClick={() => sessionDurationManager.dismiss()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') sessionDurationManager.dismiss();
              }}
              style={{
                backgroundColor: '#4caf50',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '3px',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              OK
            </span>{' '}
            Or you can Sign Out now by clicking{' '}
            <span
              role="button"
              tabIndex={0}
              onClick={() => sessionDurationManager.logoutNow()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') sessionDurationManager.logoutNow();
              }}
              style={{
                backgroundColor: '#f44336',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '3px',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              SIGN OUT
            </span>
          </p>
          <p style={{ margin: 0 }}>
            If no action is taken, then your session will be terminated automatically in{' '}
            <b style={{ color: '#d32f2f', fontSize: '1.1rem' }}>{secondsLeft}</b> seconds for
            security reasons.
          </p>
        </div>
      </MotifModal>
    </div>
  );
}
